// AdminBroadcast — Send announcements/notifications to users
// Channel: email, push, in-app, SMS. Target: all / tier / country / etc.

import { useState } from 'react';

export function AdminBroadcast() {
  const [channel, setChannel] = useState<'push' | 'email' | 'inapp' | 'sms'>('email');
  const [audience, setAudience] = useState<'all' | 'tier' | 'country' | 'kyc' | 'status'>('all');
  const [tier, setTier] = useState('Beginner');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  function send() {
    if (!subject || !body) { alert('Add subject + body'); return; }
    if (!confirm(`Send to ${audience === 'all' ? 'all 12,847 users' : audience + ' (' + tier + ')'}?`)) return;
    setSending(true);
    setTimeout(() => { setSending(false); setSent(true); }, 2200);
  }

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <div>
        <h1 className="text-[22px] font-bold text-white">Broadcast</h1>
        <p className="text-[12px] text-white/50">Send announcements, alerts, and updates to users</p>
      </div>

      <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4">
        <h2 className="text-[13px] font-bold text-white mb-3">Channel</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {([
            { key: 'email', icon: '📧', label: 'Email',   sub: '12,847 users',  color: '#007AFF' },
            { key: 'push',  icon: '🔔', label: 'Push',    sub: '8,420 enabled', color: '#FF9500' },
            { key: 'inapp', icon: '💬', label: 'In-app',  sub: 'All users',     color: '#34C759' },
            { key: 'sms',   icon: '📱', label: 'SMS',     sub: '4,210 enabled', color: '#FF3B30' },
          ] as const).map((c) => (
            <button
              key={c.key}
              onClick={() => setChannel(c.key)}
              data-testid={`channel-${c.key}`}
              className={['rounded-[12px] p-3 text-left transition',
                channel === c.key ? 'border-2 bg-white/5' : 'border border-white/5 bg-black/20 active:opacity-40'
              ].join(' ')}
              style={channel === c.key ? { borderColor: c.color } : {}}
            >
              <div className="text-[20px]">{c.icon}</div>
              <div className="mt-1 text-[12px] font-semibold text-white">{c.label}</div>
              <div className="text-[10px] text-white/50">{c.sub}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4">
        <h2 className="text-[13px] font-bold text-white mb-3">Audience</h2>
        <div className="flex items-center gap-1.5 flex-wrap">
          {(['all', 'tier', 'country', 'kyc', 'status'] as const).map((a) => (
            <button
              key={a} onClick={() => setAudience(a)}
              data-testid={`audience-${a}`}
              className={['rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
                audience === a ? 'bg-white text-black' : 'bg-white/5 text-white/60 active:bg-white/10'
              ].join(' ')}
            >
              {a}
            </button>
          ))}
          {audience === 'tier' && (
            <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="tier-pick" className="h-8 rounded-full bg-white/5 border border-white/10 px-3 text-[11px] text-white focus:outline-none">
              <option>Beginner</option>
              <option>Mid</option>
              <option>Professional</option>
            </select>
          )}
        </div>
        <div className="mt-3 rounded-[10px] bg-white/5 p-2.5 text-[11px] text-white/70">
          📊 Estimated recipients: <span className="text-white font-bold">
            {audience === 'all' ? '12,847' : audience === 'tier' ? (tier === 'Beginner' ? '8,420' : tier === 'Mid' ? '3,210' : '1,217') : audience === 'kyc' ? '11,420' : '8,420'}
          </span>
        </div>
      </div>

      <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4">
        <h2 className="text-[13px] font-bold text-white mb-3">Message</h2>
        <div className="space-y-3">
          {channel === 'email' && (
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Important: Funder Pro update" data-testid="bcast-subject" className="w-full h-10 rounded-[8px] bg-black/40 border border-white/10 px-3 text-[13px] text-white focus:outline-none placeholder:text-white/30" />
            </div>
          )}
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-1.5">Body</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder="Hi {{name}}, we have an important update..." data-testid="bcast-body" className="w-full rounded-[8px] bg-black/40 border border-white/10 p-3 text-[13px] text-white focus:outline-none placeholder:text-white/30 resize-none font-mono" />
          </div>
          <div className="rounded-[10px] bg-white/5 p-3 text-[11px] text-white/60">
            💡 <span className="font-mono">{'{{name}}'}</span>, <span className="font-mono">{'{{tier}}'}</span>, <span className="font-mono">{'{{balance}}'}</span> variables supported
          </div>
        </div>
      </div>

      <div className="rounded-[16px] bg-[#1C1C1E] border border-white/5 p-4">
        <h2 className="text-[13px] font-bold text-white mb-3">Preview</h2>
        <div className="rounded-[10px] bg-black/40 p-3 text-[12px] text-white/80">
          {channel === 'email' && <div className="font-bold text-white mb-1">{subject || '(no subject)'}</div>}
          <div className="whitespace-pre-line">{body || '(no body)'}</div>
        </div>
      </div>

      {sent ? (
        <div className="rounded-[14px] border border-[#34C759]/30 bg-[#34C759]/[0.05] p-4 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#34C759] text-white">
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <div>
            <div className="text-[14px] font-bold text-[#34C759]">Broadcast sent</div>
            <p className="text-[12px] text-white/70">12,847 recipients · Delivery in progress</p>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button onClick={() => { setSubject(''); setBody(''); }} className="flex-1 h-12 rounded-full bg-white/5 text-[15px] font-semibold text-white active:bg-white/10">Clear</button>
          <button onClick={send} disabled={sending} data-testid="send-bcast" className="flex-1 h-12 rounded-full bg-gradient-to-r from-[#FF3B30] to-[#FF9500] text-[15px] font-bold text-white disabled:opacity-40">
            {sending ? 'Sending...' : `Send via ${channel}`}
          </button>
        </div>
      )}
    </div>
  );
}
